import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Star, Trash2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SelectNative } from "@/components/ui/select-native";
import { Badge } from "@/components/ui/badge";
import { apiFetch } from "@/lib/api";
import { useAssetsQuery } from "@/lib/queries";
import { toast, useLightboxStore } from "@/stores/ui";
import type { Asset } from "@/lib/types";
import { assetTypeLabel } from "@/lib/format";
import { cn } from "@/lib/utils";

export function AssetsView() {
  const qc = useQueryClient();
  const openLightbox = useLightboxStore((s) => s.show);
  const [keyword, setKeyword] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("");

  const { items, isLoading, refetch, isFetching } = useAssetsQuery();

  const toggleFavorite = useMutation({
    mutationFn: ({ id, next }: { id: string; next: boolean }) =>
      apiFetch(`/api/v2/assets/${id}`, {
        method: "PATCH",
        body: { is_favorite: next },
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["assets"] });
      toast(vars.next ? "已收藏" : "已取消收藏");
    },
    onError: (e: Error) => toast(e.message),
  });

  const deleteAsset = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/v2/assets/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assets"] });
      toast("已软删除");
    },
    onError: (e: Error) => toast(e.message),
  });

  const filtered = useMemo(() => {
    const kw = keyword.trim();
    return items.filter((a) => {
      const okKeyword =
        !kw ||
        a.name.includes(kw) ||
        a.asset_id.includes(kw) ||
        (a.tags ?? []).some((t) => String(t).includes(kw));
      const okType = !typeFilter || a.asset_type === typeFilter;
      return okKeyword && okType;
    });
  }, [items, keyword, typeFilter]);

  return (
    <div className="mx-auto max-w-6xl">
      <section className="flex flex-col rounded-lg border border-border bg-card">
        <header className="flex items-center justify-between gap-3 border-b border-border px-5 py-4">
          <h3 className="text-sm font-semibold tracking-tight">全部素材</h3>
          <span className="font-mono text-xs text-muted-foreground">
            {filtered.length} / {items.length} 项
          </span>
        </header>
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-5 py-3">
          <Input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="关键字 / 标签搜索"
            className="max-w-[260px] flex-1"
          />
          <SelectNative
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="max-w-[180px]"
          >
            <option value="">全部类型</option>
            <option value="source_product_image">商品原图</option>
            <option value="generated_image">生成图</option>
            <option value="model_reference">模特参考</option>
            <option value="scene_reference">场景参考</option>
          </SelectNative>
          <Button
            variant="ghost"
            size="sm"
            disabled={isFetching}
            onClick={async () => {
              await refetch();
              toast("素材库已刷新");
            }}
          >
            <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
            刷新
          </Button>
        </div>

        <div className="p-5">
          {isLoading ? (
            <div className="grid place-items-center py-12 text-sm text-muted-foreground">
              加载中…
            </div>
          ) : filtered.length === 0 ? (
            <div className="grid place-items-center py-12 text-sm text-muted-foreground">
              {items.length === 0 ? "还没有素材" : "无匹配项"}
            </div>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-3">
              {filtered.map((asset) => (
                <AssetTile
                  key={asset.asset_id}
                  asset={asset}
                  onPreview={() => openLightbox(asset.file_url || asset.thumbnail_url)}
                  onToggleFavorite={() =>
                    toggleFavorite.mutate({ id: asset.asset_id, next: !asset.is_favorite })
                  }
                  onDelete={() => deleteAsset.mutate(asset.asset_id)}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function AssetTile({
  asset,
  onPreview,
  onToggleFavorite,
  onDelete,
}: {
  asset: Asset;
  onPreview: () => void;
  onToggleFavorite: () => void;
  onDelete: () => void;
}) {
  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-border bg-input transition-all hover:-translate-y-0.5 hover:border-accent/60 hover:shadow-lg">
      <button
        type="button"
        onClick={onPreview}
        className="relative aspect-square overflow-hidden bg-card"
        aria-label="预览大图"
      >
        <img
          src={asset.thumbnail_url || asset.file_url}
          alt={asset.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform group-hover:scale-[1.03]"
          onError={(e) => {
            const card = (e.currentTarget as HTMLElement).closest("article");
            if (card) (card as HTMLElement).style.display = "none";
          }}
        />
        {asset.is_favorite && (
          <Star className="absolute right-2 top-2 h-4 w-4 fill-accent text-accent" />
        )}
      </button>
      <div className="flex flex-col gap-2 p-3">
        <h3 className="line-clamp-2 text-sm font-semibold text-foreground">
          {asset.name}
        </h3>
        <div className="flex flex-wrap gap-1.5">
          {asset.category_level2 && (
            <Badge variant="default" className="text-[10px]">
              {asset.category_level2}
            </Badge>
          )}
          <Badge
            variant={asset.asset_type === "generated_image" ? "success" : "warning"}
            className="text-[10px]"
          >
            {assetTypeLabel(asset.asset_type)}
          </Badge>
        </div>
        <div className="mt-1 flex items-center gap-1.5">
          <button
            type="button"
            onClick={onToggleFavorite}
            className={cn(
              "inline-flex flex-1 items-center justify-center gap-1 rounded-md border border-border px-2 py-1 text-xs transition-colors hover:bg-white/[0.04]",
              asset.is_favorite && "text-accent",
            )}
          >
            <Star className={cn("h-3 w-3", asset.is_favorite && "fill-accent")} />
            {asset.is_favorite ? "取消" : "收藏"}
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="inline-flex items-center justify-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
            title="软删除"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>
    </article>
  );
}
